import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "@/components/Shell";
import api, { formatApiError, formatINR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/pages/admin/AdminDashboard";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { PlusCircle, Trash2 } from "lucide-react";

export default function Invoices() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const isDataEntry = user?.role === "data_entry";

  const load = () => api.get("/invoices").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/invoices/${deleteTarget.id}`);
      toast.success(`Invoice #${deleteTarget.invoice_no} deleted`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <Shell
      title={user?.role === "admin" ? "All Invoices" : isDataEntry ? "Invoices" : "My Invoices"}
      subtitle={isDataEntry ? "Enter invoices on behalf of sales team. Margin and turnover are hidden." : "All invoices you submitted, referred or closed."}
      actions={
        <Link to="/invoices/new">
          <Button className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="new-invoice-button">
            <PlusCircle size={16} className="mr-1" /> New Invoice
          </Button>
        </Link>
      }
    >
      <Card className="border-zinc-200 shadow-none rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead className="font-bold text-zinc-950">Sl</TableHead>
              <TableHead className="font-bold text-zinc-950">Invoice</TableHead>
              <TableHead className="font-bold text-zinc-950">Date</TableHead>
              <TableHead className="font-bold text-zinc-950">Customer</TableHead>
              <TableHead className="font-bold text-zinc-950">Project</TableHead>
              <TableHead className="font-bold text-zinc-950">Ref by</TableHead>
              <TableHead className="font-bold text-zinc-950">Closed by</TableHead>
              <TableHead className="font-bold text-zinc-950 text-right">Value</TableHead>
              {!isDataEntry && <TableHead className="font-bold text-zinc-950 text-right">Margin</TableHead>}
              <TableHead className="font-bold text-zinc-950 text-right">Status</TableHead>
              <TableHead className="font-bold text-zinc-950 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={isDataEntry ? 10 : 11} className="text-center py-10 text-zinc-500">No invoices yet.</TableCell></TableRow>
            )}
            {items.map((inv, i) => (
              <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                <TableCell className="tabular-nums text-zinc-500">{i + 1}</TableCell>
                <TableCell className="font-semibold font-mono text-xs">{inv.invoice_no}</TableCell>
                <TableCell>{inv.invoice_date?.slice(0, 10)}</TableCell>
                <TableCell>{inv.customer_name}</TableCell>
                <TableCell className="text-zinc-600">{inv.project || <span className="text-zinc-400">—</span>}</TableCell>
                <TableCell>{inv.customer_ref_by_name}</TableCell>
                <TableCell>{inv.deal_closed_by_name}</TableCell>
                <TableCell className="text-right tabular-nums">{formatINR(inv.invoice_value)}</TableCell>
                {!isDataEntry && <TableCell className="text-right tabular-nums">{formatINR(inv.margin)}</TableCell>}
                <TableCell className="text-right">
                  <div className="space-y-1">
                    <StatusBadge status={inv.status} />
                    {inv.rejection_reason && <div className="text-xs text-rose-700 italic">{inv.rejection_reason}</div>}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {inv.status !== "approved" && (inv.created_by_id === user?.id || user?.role === "admin") && (
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(inv)} data-testid={`delete-invoice-${inv.id}`}>
                      <Trash2 size={14} className="text-rose-600" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-invoice-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete invoice #{deleteTarget?.invoice_no}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the invoice for <span className="font-semibold">{deleteTarget?.customer_name}</span>.
              Approved invoices cannot be deleted by salespeople. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-invoice-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="delete-invoice-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
}
